import { useEffect, useRef, useState, useContext } from "react";
import usePageTitle from "../../../hooks/use-page-title";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import { Link, useNavigate, useParams } from "react-router-dom";
import Debouncer from "../../../modules/debouncer/debouncer";
import ApiUser from "../../../modules/api/user";
import Notifier from "../../../modules/notifier/notifier";
import ROLE from "../../../modules/role";
import { ROUTES } from "../../../routes";
import FGInput from "../../../components/dumb/FGInput";
import FGToggle from "../../../components/dumb/FGToggle";
import FGTextarea from "../../../components/dumb/FGTextarea";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import {
  CONST_SELECT_ROLES,
  CONST_SELECT_USER_STATUS,
  DEFAULT_SELECT_OPTION,
  USER_STATUS_ACTIVE,
} from "../../../config/constants";
import ApiGenericData from "../../../modules/api/generic-data";
import AppUserContext from "../../../context/app-user-context";
import { HiArrowLeft, HiX } from "react-icons/hi";
import Multilingual from "../../../modules/multilingual/multilingual";
import ApiProgramme from "../../../modules/api/programme";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import { MdSave } from "react-icons/md";
import { BsBoxArrowInDown } from "react-icons/bs";

const UPDATED_FIELDS = [
  "name",
  "nameEmail",
  "email",
  "roles",
  "organization",
  "jobTitle",
  "jobDescription",
  "studentId",
  "programme",
  "status",
  "isLocked",
  "lockNotification",
  "managedProgrammeNames",
] as const;
const FIELDS: { [key in (typeof UPDATED_FIELDS)[number]]: string } =
  UPDATED_FIELDS.reduce((curr, item) => ({ ...curr, [item]: "" }), {}) as any;

export default function PageUserEdit() {
  const title = "Edit User";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  useEffect(() => {
    setCustomText(title);
  }, []);
  const user = useContext(AppUserContext);
  const navigate = useNavigate();
  const { _id } = useParams();
  const refDataLoaded = useRef(false);
  const [show, setShow] = useState(false);
  const [contentChanged, setContentChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [found, setFound] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({ ...FIELDS });
  const [groupHash, setGroupHash] = useState({});

  const onChange = (e: any) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((data) => ({ ...data, [e.target.name]: value }));
    if (refDataLoaded.current) {
      setContentChanged(true);
    }
  };

  const onChangeRoles = (e: any) => {
    let value = [];
    if (e.target.value) value = e.target.value.split(",");
    setFormData((data) => ({ ...data, [e.target.name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    Debouncer.execute("SUBMIT_UPDATE_USER", async () => {
      setSending(true);
      const res = await ApiUser.update(
        _id,
        Object.keys(formData)
          .filter((i) => UPDATED_FIELDS.some((j) => j === i))
          .map((i) => ({
            key: i,
            value: formData[i],
          }))
          .reduce((curr, item) => ({ ...curr, [item.key]: item.value }), {})
      );

      if (res.status) {
        Notifier.success(res.message);
        setSending(false);
        setContentChanged(false);
      } else {
        setSending(false);
        Notifier.error(res.message);
      }
    });
  };

  const roleOptions = () => {
    let roles = CONST_SELECT_ROLES;
    if (user.role === ROLE.ADMIN) {
      return roles.filter((role) =>
        [ROLE.ADMIN, ROLE.PROGRAMME_ADMIN].some((value) => value === role.value)
      );
    } else {
      return roles.filter((role) =>
        [ROLE.STUDENT, ROLE.SUPERVISOR, ROLE.INDUSTRY_SUPERVISOR].some(
          (value) => value === role.value
        )
      );
    }
  };

  const checkStatusValue = (obj: any) => {
    return {
      ...obj,
      status: obj.status ?? USER_STATUS_ACTIVE,
    };
  };

  const getUserGroups = () => {
    const groupIds = Object.keys(
      typeof groupHash === "object" ? groupHash : {}
    );
    return groupIds.map((groupId) => groupHash[groupId]);
  };

  useEffect(() => {
    setLoading(true);
    ApiUser.get(_id).then((res) => {
      if (res.status) {
        setFound(true);
        setFormData(checkStatusValue(res.data));
        setGroupHash(
          typeof res.data.groupHash === "object" ? res.data.groupHash : {}
        );
        setTimeout(() => {
          refDataLoaded.current = true;
        }, 250);
      } else {
        setFound(false);
      }

      setLoading(false);
    });
  }, []);

  return (
    <div data-comp="PageUserRegist">
      {/* <h2 className="fs-title mb-3 hide-mobile-down">{title}</h2> */}

      {/* <div className="cg__actions hide-mobile-down">
        <Link
          to={ROUTES.PORTAL_USERS + `?role=${formData.role}`}
          className="btn btn-primary"
          onClick={(e) => {
            if (
              contentChanged &&
              !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
            ) {
              e.preventDefault();
            }
          }}
        >
          <HiArrowLeft /> Back
        </Link>
      </div> */}

      {loading && <p className="alert alert-info">Loading data...</p>}

      {!loading && !found && (
        <p className="alert alert-info">User not found.</p>
      )}

      {!loading && found && (
        <Modal
          className="modal-generic"
          show={show}
          onHide={() => setShow(false)}
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Assigned Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <DataTable
              data={getUserGroups()}
              columns={[
                {
                  id: "programme",
                  name: "Programme",
                  cell: (row: any) => row.cohort?.programme?.name ?? ``,
                },
                {
                  id: "cohort",
                  name: "Cohort",
                  cell: (row: any) => row.cohort?.name ?? ``,
                },
                {
                  id: "group",
                  name: "Group",
                  cell: (row: any) => row.name,
                },
              ]}
            ></DataTable>
          </Modal.Body>
        </Modal>
      )}

      {!loading && found && (
        <form className="form-register" onSubmit={handleSubmit}>
          <div className="row" data-comp="PageuserRegistManual">
            <div className="col-md-6">
              <FGInput
                label="Name"
                name="name"
                value={formData.name}
                onChange={onChange}
                required
                autoFocus
              />
            </div>
            <div className="col-md-6">
              <FGInput
                label="SIT ID Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={onChange}
                required
              />
            </div>
            <div className="col-md-6">
              <FGInput
                label="SIT Email Address"
                name="nameEmail"
                type="email"
                value={formData.nameEmail}
                onChange={onChange}
              />
            </div>
            {user.role !== ROLE.ADMIN && (
              <div className="col-md-6">
                <FGInput
                  label="Student ID"
                  name="studentId"
                  value={formData.studentId}
                  onChange={onChange}
                />
              </div>
            )}
            <div className="col-md-6">
              <FGInput
                label="Job title"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={onChange}
              />
            </div>
            <div className="col-md-6">
              <FGDropdownApi
                multiple
                label="Roles"
                name="roles"
                options={CONST_SELECT_ROLES}
                defaultValue={
                  Array.isArray(formData.roles)
                    ? formData.roles.join(",")
                    : null
                }
                onChange={onChangeRoles}
              />
            </div>
            {user.role !== ROLE.ADMIN && (
              <div className="col-md-6">
                <FGDropdownApi
                  label="Organization"
                  name="organization"
                  defaultValue={formData.organization}
                  api={ApiGenericData.selectQuery(
                    "Organization",
                    "organization",
                    {
                      ...DEFAULT_SELECT_OPTION,
                    }
                  )}
                  onChange={onChange}
                />
              </div>
            )}
            <div className="col-md-6">
              <FGDropdownApi
                label="Programme"
                name="programme"
                defaultValue={formData.programme}
                api={ApiProgramme.getOptions}
                onChange={onChange}
              />
            </div>
            {found && formData.roles.includes(ROLE.PROGRAMME_ADMIN) && (
              <div className="col-md-6">
                <FGTextarea
                  label="Managed Programmes"
                  rows={2}
                  readOnly
                  value={formData.managedProgrammeNames}
                />
              </div>
            )}
            <hr />
            {(formData.roles.includes(ROLE.ADMIN) ||
              formData.roles.includes(ROLE.PROGRAMME_ADMIN)) && (
              <>
                <div className="col-md-12">
                  <FGToggle
                    label="Allow User Lock Notification"
                    desc={
                      "Enable if we'd like to send notification to this user whenever a relevant user is locked"
                    }
                    name="lockNotification"
                    checked={!!formData.lockNotification}
                    className="border-0 p-0"
                    onChange={onChange}
                    readOnly
                  />
                </div>
                <hr />
              </>
            )}
            <div className="col-md-12">
              <FGDropdownApi
                variant="normal"
                label="Status"
                name="status"
                defaultValue={formData.status}
                options={CONST_SELECT_USER_STATUS}
                onChange={onChange}
              />
            </div>
            <hr />
            <div className="col-md-12">
              <FGToggle
                label="Lock"
                desc={"When enabled, the system will lock this user."}
                name="isLocked"
                checked={!!formData.isLocked}
                className="border-0 p-0"
                onChange={onChange}
                readOnly
              />
            </div>
            <hr />
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShow((_) => !_)}
              >
                View Assigned Group
              </button>
            </div>
            <hr />
          </div>
          <div>
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={sending}
            >
              <BsBoxArrowInDown className="icon-pos-upper" /> Save
            </button>{" "}
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                if (window.history.length === 1) {
                  navigate(
                    ROUTES.PORTAL_USERS +
                      `?role=${
                        formData.roles.length === 1 ? formData.roles[0] : ""
                      }`
                  );
                } else {
                  navigate(-1);
                }
              }}
            >
              <HiX /> Cancel
            </button>
          </div>
        </form>
      )}

      <FooterBoxAction
        backUrl={
          ROUTES.PORTAL_USERS +
          `?role=${formData.roles.length === 1 ? formData.roles[0] : ""}`
        }
        onBackUrlClick={(e) => {
          if (
            contentChanged &&
            !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
          ) {
            e.preventDefault();
          }
        }}
      />
    </div>
  );
}
